import * as Hub from '~/ogm-hub';

export const orTagChipClassName = 'meta-product-tags-or-chip';

export const META_PRODUCT_NAME_MAX_LENGTH = 255;

/**
 * Builds a master item name: category → primary → general tag(s) → packaging.
 * Packaging is always last; general segments are added until the 255-char limit
 * (counting packaging when present).
 */
export function joinMetaProductNameSegments(
	category: string | undefined,
	primary: string | undefined,
	generalSegments: string[],
	packaging: string | undefined,
	maxLength = META_PRODUCT_NAME_MAX_LENGTH
): { name: string; allGeneralsIncluded: boolean } {
	const segments: string[] = [];
	const appendSegment = (text: string | undefined) => {
		const trimmed = text?.trim();
		if (trimmed) {
			segments.push(trimmed);
		}
	};

	appendSegment(category);
	appendSegment(primary);

	const packagingText = packaging?.trim() ?? '';
	const generals = generalSegments
		.map((segment) => segment.trim())
		.filter((segment) => segment.length > 0);

	let generalsIncluded = 0;
	for (const generalSegment of generals) {
		const testSegments = [...segments, generalSegment];
		if (packagingText) {
			testSegments.push(packagingText);
		}
		if (testSegments.join('-').length <= maxLength) {
			segments.push(generalSegment);
			generalsIncluded++;
		} else {
			break;
		}
	}

	if (packagingText) {
		segments.push(packagingText);
	}

	const result = segments.join('-');
	const name =
		result.length > maxLength ? result.substring(0, maxLength) : result;

	return {
		name,
		allGeneralsIncluded: generalsIncluded === generals.length,
	};
}

const tagTypePriority = {
	[Hub.TagType.Category]: 1,
	[Hub.TagType.Primary]: 2,
	[Hub.TagType.General]: 3,
	[Hub.TagType.Packaging]: 4,
};

export function compareTagDisplayOrder(
	a: { tagType: Hub.TagType; name: string },
	b: { tagType: Hub.TagType; name: string }
): number {
	const priorityA = tagTypePriority[a.tagType] || 999;
	const priorityB = tagTypePriority[b.tagType] || 999;
	if (priorityA !== priorityB) {
		return priorityA - priorityB;
	}

	const aStartsWithNumber = /^\d/.test(a.name);
	const bStartsWithNumber = /^\d/.test(b.name);
	if (aStartsWithNumber !== bStartsWithNumber) {
		return aStartsWithNumber ? 1 : -1;
	}

	return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

function compareTags(a: Hub.Tag, b: Hub.Tag): number {
	return compareTagDisplayOrder(a, b);
}

function tagsById(tags: Hub.Tag[]): Map<number, Hub.Tag> {
	return new Map(tags.map((tag) => [tag.id, tag]));
}

export function tagIdsOnLinks(tagLinks: Hub.MetaProductTagLink[]): number[] {
	const tagIds = new Set<number>();
	tagLinks.forEach((tagLink) => {
		tagIds.add(tagLink.tagId);
		(tagLink.orTagIds ?? []).forEach((orTagId) => tagIds.add(orTagId));
	});
	return Array.from(tagIds);
}

/** Included master-item tag ids (anchors and OR alternates). */
export function includedMasterTagIds(
	tagLinks: Hub.MetaProductTagLink[] = []
): Set<number> {
	return new Set(
		tagIdsOnLinks(tagLinks.filter((tagLink) => !tagLink.excluded))
	);
}

/**
 * Whether a source master-item OR/single chip should show selected on rearrange.
 * Product-row picks do not select the parent OR chip on the source item.
 */
export function isSourceTagGroupSelectedForMove(
	group: TagGroup,
	selectedTags: { value: string; fromProduct?: boolean }[]
): boolean {
	const selectedByValue = new Map(
		selectedTags.map((tag) => [tag.value, tag])
	);
	const anchorValue = group.tag.id.toString();
	if (group.orTags.length === 0) {
		return selectedByValue.has(anchorValue);
	}

	const memberValues = [
		anchorValue,
		...group.orTags.map((tag) => tag.id.toString()),
	];
	if (
		memberValues.some((value) => selectedByValue.get(value)?.fromProduct)
	) {
		return false;
	}

	const anchorPick = selectedByValue.get(anchorValue);
	if (anchorPick && !anchorPick.fromProduct) {
		return true;
	}

	return memberValues.every((value) => selectedByValue.has(value));
}

/** Merges source master-item and draft tag links for OR lookup on rearrange. */
export function tagLinkPoolForRearrangeDraft(
	sourceTagLinks: Hub.MetaProductTagLink[][],
	draftMetaProductTagLinks: Hub.MetaProductTagLink[] = []
): Hub.MetaProductTagLink[] {
	const pooled: Hub.MetaProductTagLink[] = [];
	sourceTagLinks.forEach((links) => {
		pooled.push(...links);
	});
	pooled.push(...draftMetaProductTagLinks);
	return pooled;
}

/** Tag ids that belong to an included OR group on the master item. */
export function orGroupedMasterTagIds(
	tagLinks: Hub.MetaProductTagLink[] = []
): Set<number> {
	const ids = new Set<number>();
	tagLinks
		.filter((tagLink) => !tagLink.excluded)
		.forEach((tagLink) => {
			const alternates = (tagLink.orTagIds ?? []).filter(
				(orTagId) => orTagId !== tagLink.tagId
			);
			if (alternates.length === 0) {
				return;
			}
			ids.add(tagLink.tagId);
			alternates.forEach((orTagId) => ids.add(orTagId));
		});
	return ids;
}

function orTagsForLink(
	tagLink: Hub.MetaProductTagLink,
	resolvedTags: Map<number, Hub.Tag>
): Hub.Tag[] {
	return (tagLink.orTagIds ?? [])
		.filter((orTagId) => orTagId !== tagLink.tagId)
		.map((orTagId) => resolvedTags.get(orTagId))
		.filter((tag): tag is Hub.Tag => tag != null)
		.sort(compareTags);
}

export function formatTagGroupLabel(
	typeName: string | undefined,
	primaryName: string,
	orTags: Hub.Tag[]
): string {
	const names = [
		primaryName,
		...orTags.map((tag) => {
			const groupType = Hub.TagType[tag.tagType];
			if (groupType && groupType !== typeName) {
				return `${groupType}: ${tag.name}`;
			}
			return tag.name;
		}),
	];
	const joinedNames = names.join(' OR ');
	return typeName ? `${typeName}: ${joinedNames}` : joinedNames;
}

export interface TagGroup {
	tagLink: Hub.MetaProductTagLink;
	tag: Hub.Tag;
	orTags: Hub.Tag[];
}

function isOrAlternateTagLink(
	tagLink: Hub.MetaProductTagLink,
	allLinks: Hub.MetaProductTagLink[]
): boolean {
	return allLinks.some(
		(link) =>
			link.tagId !== tagLink.tagId &&
			(link.orTagIds ?? []).includes(tagLink.tagId)
	);
}

export function buildTagGroups(
	tagLinks: Hub.MetaProductTagLink[] = [],
	tags: Hub.Tag[] = []
): TagGroup[] {
	const resolvedTags = tagsById(tags);
	const groups: TagGroup[] = [];

	tagLinks.forEach((tagLink) => {
		if (isOrAlternateTagLink(tagLink, tagLinks)) {
			return;
		}
		const tag = resolvedTags.get(tagLink.tagId);
		if (!tag) {
			return;
		}
		groups.push({
			tagLink,
			tag,
			orTags: orTagsForLink(tagLink, resolvedTags),
		});
	});

	return groups.sort((a, b) => compareTags(a.tag, b.tag));
}

export function tagOptionsForOrGroup(
	primary: { value: string; label: string; excluded: boolean },
	tagLinks: Hub.MetaProductTagLink[],
	resolvedTags: Hub.Tag[]
): { value: string; label: string; excluded: boolean }[] {
	const orTags = orTagsForSelectedTag(primary.value, tagLinks, resolvedTags);
	return [
		primary,
		...orTags.map((tag) => ({
			value: tag.id.toString(),
			label: tag.name,
			excluded: primary.excluded,
		})),
	];
}

export function allOrGroupMemberOptions(
	primaries: { value: string; label: string; excluded: boolean }[],
	tagLinks: Hub.MetaProductTagLink[],
	resolvedTags: Hub.Tag[]
): { value: string; label: string; excluded: boolean }[] {
	const members: { value: string; label: string; excluded: boolean }[] = [];
	primaries.forEach((primary) => {
		tagOptionsForOrGroup(primary, tagLinks, resolvedTags).forEach((option) => {
			members.push(option);
		});
	});
	return members;
}

export function orTagsForSelectedTag(
	tagIdValue: string,
	tagLinks: Hub.MetaProductTagLink[],
	resolvedTags: Hub.Tag[]
): Hub.Tag[] {
	const tagId = parseInt(tagIdValue, 10);
	const tagLink = tagLinks.find((link) => link.tagId === tagId);
	if (!tagLink) {
		return [];
	}
	return orTagsForLink(tagLink, tagsById(resolvedTags));
}

export function singleTypeOrGroup(
	selected: { value: string; label: string; excluded: boolean }[],
	current: { value: string; excluded: boolean } | null
): {
	primary: { value: string; label: string; excluded: boolean };
	orTagIds: number[];
} | null {
	if (selected.length === 0) {
		return null;
	}

	const primary =
		selected.find((tag) => tag.value === current?.value) ?? selected[0];
	const primaryId = parseInt(primary.value, 10);
	return {
		primary: {
			value: primary.value,
			label: primary.label,
			excluded: current?.excluded ?? false,
		},
		orTagIds: selected
			.map((tag) => parseInt(tag.value, 10))
			.filter((tagId) => tagId !== primaryId),
	};
}

export function generalGroupsFromChecked(
	checked: { value: string; label: string; excluded: boolean }[],
	currentPrimaries: { value: string; label: string; excluded: boolean }[],
	tagLinks: Hub.MetaProductTagLink[]
): {
	primaries: { value: string; label: string; excluded: boolean }[];
	links: Hub.MetaProductTagLink[];
} {
	const checkedById = new Map(
		checked.map((tag) => [parseInt(tag.value, 10), tag])
	);
	const used = new Set<number>();
	const primaries: { value: string; label: string; excluded: boolean }[] = [];
	const links: Hub.MetaProductTagLink[] = [];

	const keepGroup = (
		primary: { value: string; label: string; excluded: boolean },
		orTagIds: number[]
	) => {
		const primaryId = parseInt(primary.value, 10);
		used.add(primaryId);
		orTagIds.forEach((tagId) => used.add(tagId));
		primaries.push(primary);
		links.push({
			tagId: primaryId,
			excluded: primary.excluded,
			orTagIds,
		});
	};

	currentPrimaries.forEach((primary) => {
		const primaryId = parseInt(primary.value, 10);
		const remainingOr = (
			tagLinks.find((link) => link.tagId === primaryId)?.orTagIds ?? []
		).filter((tagId) => tagId !== primaryId && checkedById.has(tagId));

		if (checkedById.has(primaryId)) {
			keepGroup(primary, remainingOr);
			return;
		}

		const nextPrimary = checkedById.get(remainingOr[0]);
		if (!nextPrimary) {
			return;
		}

		keepGroup(
			{
				value: nextPrimary.value,
				label: nextPrimary.label,
				excluded: primary.excluded,
			},
			remainingOr.slice(1)
		);
	});

	checked.forEach((tag) => {
		const tagId = parseInt(tag.value, 10);
		if (used.has(tagId)) {
			return;
		}
		keepGroup({ ...tag, excluded: false }, []);
	});

	return { primaries, links };
}

export function tagLinksStillSelected(
	tagLinks: Hub.MetaProductTagLink[],
	selectedTagIds: number[]
): Hub.MetaProductTagLink[] {
	const selected = new Set(selectedTagIds);
	return tagLinks.filter((tagLink) => selected.has(tagLink.tagId));
}

export function tagLinksFromTagOptions(
	options: { value: string; excluded: boolean; fromProduct?: boolean }[],
	allTags: Hub.Tag[],
	existingLinks: Hub.MetaProductTagLink[] = []
): Hub.MetaProductTagLink[] {
	const included: Hub.Tag[] = [];
	const excluded: Hub.Tag[] = [];
	const skipOrPreservationForTagIds = new Set<number>();

	options.forEach((option) => {
		const tagId = parseInt(option.value, 10);
		const tag = allTags.find((t) => t.id === tagId);
		if (!tag) {
			return;
		}
		if (option.fromProduct) {
			skipOrPreservationForTagIds.add(tagId);
		}
		if (option.excluded) {
			excluded.push(tag);
		} else {
			included.push(tag);
		}
	});

	return tagLinksPreservingOrTags(
		included,
		excluded,
		existingLinks,
		skipOrPreservationForTagIds
	);
}

/** Resolves OR membership from existing links, then builds display-order chip groups. */
export function tagGroupsFromTagOptions(
	options: { value: string; excluded: boolean; fromProduct?: boolean }[],
	allTags: Hub.Tag[],
	existingLinks: Hub.MetaProductTagLink[] = []
): TagGroup[] {
	return buildTagGroups(
		tagLinksFromTagOptions(options, allTags, existingLinks),
		allTags
	);
}

/**
 * Union orTagIds from pool links with this anchor tag id (same id can appear on
 * multiple master items; empty orTagIds on one must not erase OR on another).
 */
function orTagIdsFromPoolForAnchor(
	tagId: number,
	existingLinks: Hub.MetaProductTagLink[]
): number[] {
	const orTagIds = new Set<number>();
	existingLinks.forEach((link) => {
		if (link.tagId !== tagId) {
			return;
		}
		(link.orTagIds ?? []).forEach((id) => {
			if (id !== tagId) {
				orTagIds.add(id);
			}
		});
	});
	return Array.from(orTagIds);
}

export interface RearrangeMoveTagOption {
	value: string;
	label: string;
	excluded: boolean;
	fromProduct?: boolean;
}

/** One chip per OR group when the whole group is selected; otherwise only explicit picks. */
export function collapseSelectedTagsForMove(
	selectedTags: RearrangeMoveTagOption[],
	tagLinks: Hub.MetaProductTagLink[],
	allTags: Hub.Tag[]
): RearrangeMoveTagOption[] {
	if (selectedTags.length === 0) {
		return [];
	}

	const selectedByValue = new Map(
		selectedTags.map((tag) => [tag.value, tag])
	);
	const groups = buildTagGroups(tagLinks, allTags);
	const result: RearrangeMoveTagOption[] = [];
	const consumed = new Set<string>();

	groups.forEach((group) => {
		const memberValues = [
			group.tag.id.toString(),
			...group.orTags.map((tag) => tag.id.toString()),
		];
		const selectedMembers = memberValues.filter((value) =>
			selectedByValue.has(value)
		);
		if (selectedMembers.length === 0) {
			return;
		}

		const fullGroupSelected = isSourceTagGroupSelectedForMove(
			group,
			selectedTags
		);
		const anyFromProduct = selectedMembers.some(
			(value) => selectedByValue.get(value)?.fromProduct
		);

		if (fullGroupSelected && !anyFromProduct) {
			result.push({
				value: group.tag.id.toString(),
				label: group.tag.name,
				excluded: group.tagLink.excluded,
			});
			memberValues.forEach((value) => consumed.add(value));
			return;
		}

		selectedMembers.forEach((value) => {
			if (consumed.has(value)) {
				return;
			}
			const tag = selectedByValue.get(value);
			if (tag) {
				result.push(tag);
				consumed.add(value);
			}
		});
	});

	selectedTags.forEach((tag) => {
		if (!consumed.has(tag.value)) {
			result.push(tag);
		}
	});

	return result;
}

export function tagLinksPreservingOrTags(
	includedTags: Hub.Tag[],
	excludedTags: Hub.Tag[],
	existingLinks: Hub.MetaProductTagLink[] = [],
	skipOrPreservationForTagIds: ReadonlySet<number> = new Set()
): Hub.MetaProductTagLink[] {
	const toLink = (tag: Hub.Tag, excluded: boolean): Hub.MetaProductTagLink => ({
		tagId: tag.id,
		excluded,
		orTagIds:
			excluded ||
			skipOrPreservationForTagIds.has(tag.id)
				? []
				: orTagIdsFromPoolForAnchor(tag.id, existingLinks),
	});

	return [
		...includedTags.map((tag) => toLink(tag, false)),
		...excludedTags.map((tag) => toLink(tag, true)),
	];
}

export function resolvedTagsKeepingOrTags(
	primaryTags: Hub.Tag[],
	tagLinks: Hub.MetaProductTagLink[],
	existingResolvedTags: Hub.Tag[]
): Hub.Tag[] {
	const primaryTagIds = new Set(primaryTags.map((tag) => tag.id));
	const orTagIds = new Set<number>();
	tagLinks.forEach((tagLink) => {
		(tagLink.orTagIds ?? []).forEach((orTagId) => {
			if (!primaryTagIds.has(orTagId)) {
				orTagIds.add(orTagId);
			}
		});
	});

	const retainedOrTags = existingResolvedTags.filter((tag) =>
		orTagIds.has(tag.id)
	);
	return [...primaryTags, ...retainedOrTags];
}

interface SelectedSearchTag {
	value: string;
	excluded: boolean;
}

export function buildTagSearchRequest(
	supplierCode: string,
	selected: {
		category?: SelectedSearchTag | null;
		primary?: SelectedSearchTag | null;
		packaging?: SelectedSearchTag | null;
		general?: SelectedSearchTag[] | null;
	},
	tagLinks: Hub.MetaProductTagLink[] = []
): Hub.SearchProductsV3Request {
	const alternateTagIds = (tagId: number) =>
		(tagLinks.find((link) => link.tagId === tagId)?.orTagIds ?? []).filter(
			(orTagId) => orTagId !== tagId
		);
	const includedTagIds = (option?: SelectedSearchTag | null) => {
		if (!option || option.excluded) {
			return [];
		}
		const tagId = parseInt(option.value, 10);
		return [tagId, ...alternateTagIds(tagId)];
	};
	const excludedTagId = (option?: SelectedSearchTag | null) =>
		option?.excluded ? parseInt(option.value, 10) : null;
	const general = selected.general ?? [];

	return {
		andTerms: [],
		orTerms: [],
		notTerms: [],
		corpSupplierCode: [supplierCode],
		brand: '',
		categoryTagIds: includedTagIds(selected.category),
		excludedCategoryTagId: excludedTagId(selected.category),
		packagingTagIds: includedTagIds(selected.packaging),
		excludedPackagingTagId: excludedTagId(selected.packaging),
		primaryTagIds: includedTagIds(selected.primary),
		excludedPrimaryTagId: excludedTagId(selected.primary),
		generalTags: general
			.filter((tag) => !tag.excluded)
			.map((tag) => {
				const tagId = parseInt(tag.value, 10);
				return { tagId, alternateTagIds: alternateTagIds(tagId) };
			}),
		excludedGeneralTagIds: general
			.filter((tag) => tag.excluded)
			.map((tag) => parseInt(tag.value, 10)),
		syncStatus: true,
		includeNetUnitData: false,
		skip: 0,
		take: 100,
		sortBy: null,
	};
}

export interface DraggedTag {
	tagId: number;
	tagType: Hub.TagType;
	name: string;
	orTagIds: number[];
	fromSelection: boolean;
	excluded: boolean;
}

export function canDropTag(
	dragged: DraggedTag,
	targetTagId: number,
	targetTagType: Hub.TagType,
	targetExcluded: boolean
): boolean {
	if (dragged.excluded || targetExcluded) {
		return false;
	}
	return dragged.tagType === targetTagType && dragged.tagId !== targetTagId;
}

export function mergeDroppedTag(
	tagLinks: Hub.MetaProductTagLink[],
	targetTagId: number,
	targetExcluded: boolean,
	droppedTagId: number,
	droppedOrTagIds: number[]
): Hub.MetaProductTagLink[] | null {
	if (targetExcluded) {
		return null;
	}

	const droppedLink = tagLinks.find((link) => link.tagId === droppedTagId);
	if (droppedLink?.excluded) {
		return null;
	}

	if (droppedTagId === targetTagId) {
		return null;
	}

	const addedIds = [droppedTagId, ...droppedOrTagIds].filter(
		(tagId) => tagId !== targetTagId
	);
	const added = new Set(addedIds);
	const target = tagLinks.find((link) => link.tagId === targetTagId);
	const alreadyOnTarget = addedIds.every((tagId) =>
		(target?.orTagIds ?? []).includes(tagId)
	);
	const changesAnotherLink = tagLinks.some(
		(link) =>
			link.tagId === droppedTagId ||
			(link.tagId !== targetTagId &&
				(link.orTagIds ?? []).some((tagId) => added.has(tagId)))
	);
	if (alreadyOnTarget && !changesAnotherLink) {
		return null;
	}

	const nextLinks = tagLinks
		.filter((link) => link.tagId !== droppedTagId)
		.map((link) => {
			if (link.tagId === targetTagId) {
				return link;
			}
			return {
				...link,
				orTagIds: (link.orTagIds ?? []).filter((tagId) => !added.has(tagId)),
			};
		});

	const nextOrTagIds = Array.from(
		new Set([...(target?.orTagIds ?? []), ...addedIds])
	);
	if (!target) {
		nextLinks.push({
			tagId: targetTagId,
			excluded: targetExcluded,
			orTagIds: nextOrTagIds,
		});
		return nextLinks;
	}

	return nextLinks.map((link) =>
		link.tagId === targetTagId ? { ...link, orTagIds: nextOrTagIds } : link
	);
}

export function tagIdsIncludingOrAlternatives(
	tagIds: number[],
	tagLinks: Hub.MetaProductTagLink[]
): Set<number> {
	const ids = new Set(tagIds);
	tagLinks.forEach((tagLink) => {
		if (!ids.has(tagLink.tagId)) {
			return;
		}
		(tagLink.orTagIds ?? []).forEach((orTagId) => ids.add(orTagId));
	});
	return ids;
}
